import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Loader2, HeartCrack, Gift, Shield, Award, Star, Crown, Diamond, Gem } from 'lucide-react';
import { useDailyBonusStatus, useClaimDailyBonus, CycleProgressDay } from '@/hooks/useDailyBonus';
import { useDailyBonus } from '@/contexts/DailyBonusContext';
import { useVerification } from '@/contexts/VerificationContext';
import { useVip } from '@/contexts/VipContext';
import { useToast } from '@/hooks/use-toast';
import { CountdownTimer } from './CountdownTimer';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';

interface DailyBonusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Vibrant sparkle/star SVG - golden glow on dark background
const Star = ({ className = '', size = 24, delay = 0 }: { className?: string; size?: number; delay?: number }) => (
  <svg
    className={`absolute animate-pulse drop-shadow-[0_0_12px_rgba(251,191,36,0.9)] ${className}`}
    style={{ animationDelay: `${delay}ms` }}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z"
      fill="#FCD34D"
    />
  </svg>
);

// Floating coin wrapper for background decoration - uses SVG badges with glow
const FloatingCoin = ({ className = '', size = 'md', delay = 0, type = 'gc' }: { className?: string; size?: 'sm' | 'md' | 'lg'; delay?: number; type?: 'gc' | 'sc' }) => {
  const svgSize = size === 'sm' ? 20 : size === 'lg' ? 32 : 26;
  return (
    <div
      className={`absolute animate-float ${type === 'gc' ? 'drop-shadow-[0_0_15px_rgba(251,191,36,0.6)]' : 'drop-shadow-[0_0_15px_rgba(16,185,129,0.6)]'} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {type === 'gc' ? <GCBadgeSVG size={svgSize} /> : <SCBadgeSVG size={svgSize} />}
    </div>
  );
};

// Subtle radial glow background - warm gold/purple accents
const RadialGlow = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Central golden glow */}
    <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[800px] h-[800px]">
      <div className="w-full h-full rounded-full bg-gradient-radial from-amber-400/25 via-amber-500/10 to-transparent blur-3xl" />
    </div>
    {/* Secondary purple accent glow */}
    <div className="absolute bottom-0 left-0 w-[600px] h-[600px]">
      <div className="w-full h-full rounded-full bg-gradient-radial from-purple-400/15 via-purple-500/5 to-transparent blur-3xl" />
    </div>
    {/* Subtle top-right accent */}
    <div className="absolute top-0 right-0 w-[400px] h-[400px]">
      <div className="w-full h-full rounded-full bg-gradient-radial from-amber-300/20 via-orange-400/8 to-transparent blur-2xl" />
    </div>
  </div>
);

// Progressive treasure icons - from small pot to overflowing chest like the references!
const ProgressiveChest = ({ 
  day, 
  claimed = false, 
  current = false, 
  size = 52 
}: { 
  day: number; 
  claimed?: boolean; 
  current?: boolean; 
  size?: number;
}) => {
  const id = `treasure-${day}-${Math.random().toString(36).substr(2, 9)}`;
  const isActive = current || claimed;
  
  // Colors based on state
  const brown = claimed 
    ? { main: '#166534', dark: '#14532D', light: '#22C55E' }
    : current 
    ? { main: '#8B4513', dark: '#5D2E0C', light: '#A0522D' }
    : { main: '#6B5B4F', dark: '#4A3F37', light: '#8B7B6F' };
  
  const goldTrim = isActive 
    ? { main: '#DAA520', light: '#FFD700', dark: '#B8860B' }
    : { main: '#9CA3AF', light: '#D1D5DB', dark: '#6B7280' };

  // Simple cartoony gold coin - just an oval with shine, no letters!
  const Coin = ({ x, y, r, tilt = 0 }: { x: number; y: number; r: number; tilt?: number }) => (
    <g transform={`rotate(${tilt} ${x} ${y})`} opacity={isActive ? 1 : 0.55}>
      <ellipse cx={x} cy={y + r * 0.15} rx={r} ry={r * 0.4} fill="#B8860B" />
      <ellipse cx={x} cy={y} rx={r} ry={r * 0.85} fill="#FFD700" />
      <ellipse cx={x} cy={y} rx={r * 0.8} ry={r * 0.68} fill="#FFDF00" />
      <ellipse cx={x - r * 0.25} cy={y - r * 0.2} rx={r * 0.4} ry={r * 0.3} fill="#FFF8B0" opacity="0.8" />
      <ellipse cx={x - r * 0.35} cy={y - r * 0.3} rx={r * 0.15} ry={r * 0.1} fill="white" opacity="0.9" />
    </g>
  );

  // Green gem for sweep coins
  const Gem = ({ x, y, r }: { x: number; y: number; r: number }) => (
    <g opacity={isActive ? 1 : 0.55}>
      <ellipse cx={x} cy={y + r * 0.3} rx={r * 0.6} ry={r * 0.2} fill="rgba(0,0,0,0.2)" />
      <polygon points={`${x},${y - r} ${x + r * 0.7},${y} ${x + r * 0.4},${y + r * 0.7} ${x - r * 0.4},${y + r * 0.7} ${x - r * 0.7},${y}`} fill="#22C55E" />
      <polygon points={`${x},${y - r} ${x + r * 0.7},${y} ${x},${y + r * 0.2} ${x - r * 0.7},${y}`} fill="#4ADE80" />
      <polygon points={`${x - r * 0.3},${y - r * 0.5} ${x},${y - r * 0.3} ${x - r * 0.2},${y}`} fill="white" opacity="0.6" />
    </g>
  );

  // Day 1: Small pot with few coins
  if (day === 1) {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_12px_rgba(251,191,36,0.7)]' : ''}>
        {/* Pot */}
        <ellipse cx="32" cy="54" rx="16" ry="4" fill={brown.dark} />
        <path d="M18 32 Q16 54 20 54 L44 54 Q48 54 46 32 Z" fill={brown.main} />
        <path d="M18 32 Q16 54 20 54 L44 54 Q48 54 46 32 L18 32" fill={brown.light} />
        <ellipse cx="32" cy="32" rx="14" ry="5" fill={brown.main} />
        <ellipse cx="32" cy="32" rx="12" ry="4" fill={brown.dark} />
        {/* Rim */}
        <ellipse cx="32" cy="30" rx="15" ry="4" fill={goldTrim.main} />
        <ellipse cx="32" cy="30" rx="13" ry="3" fill={goldTrim.light} opacity="0.5" />
        {/* Coins inside */}
        <Coin x={28} y={28} r={5} tilt={-10} />
        <Coin x={36} y={27} r={5} tilt={15} />
        <Coin x={32} y={24} r={5.5} tilt={0} />
        {claimed && <circle cx="50" cy="14" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M46 14 L49 17 L55 11" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 2: Bigger pot overflowing
  if (day === 2) {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_12px_rgba(251,191,36,0.7)]' : ''}>
        {/* Pot shadow */}
        <ellipse cx="32" cy="58" rx="18" ry="4" fill="rgba(0,0,0,0.2)" />
        {/* Pot body */}
        <path d="M14 30 Q10 58 18 58 L46 58 Q54 58 50 30 Z" fill={brown.main} />
        <path d="M14 30 L18 30 Q12 58 18 58 L46 58 Q52 58 46 30 L50 30 Q54 58 46 60 L18 60 Q10 58 14 30 Z" fill={brown.dark} opacity="0.5" />
        {/* Pot opening */}
        <ellipse cx="32" cy="30" rx="18" ry="6" fill={brown.dark} />
        <ellipse cx="32" cy="28" rx="16" ry="5" fill="#1a1410" />
        {/* Gold rim */}
        <ellipse cx="32" cy="28" rx="18" ry="5" fill={goldTrim.main} />
        <ellipse cx="32" cy="27" rx="16" ry="4" fill={goldTrim.light} opacity="0.4" />
        {/* Coins pile */}
        <Coin x={24} y={26} r={4.5} tilt={-20} />
        <Coin x={32} y={27} r={5} tilt={5} />
        <Coin x={40} y={26} r={4.5} tilt={20} />
        <Coin x={28} y={22} r={5} tilt={-10} />
        <Coin x={36} y={21} r={5} tilt={10} />
        <Coin x={32} y={17} r={5.5} tilt={0} />
        {/* Spilled coin */}
        <Coin x={52} y={52} r={4} tilt={30} />
        {claimed && <circle cx="52" cy="12" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M48 12 L51 15 L57 9" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 3: Small WOODEN CRATE - cute compact box with coins peeking out
  if (day === 3) {
    const woodColor = claimed 
      ? { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', plank: '#15803D' }
      : current 
      ? { main: '#CD853F', dark: '#8B4513', light: '#DEB887', plank: '#A0522D' }
      : { main: '#8B7355', dark: '#6B5344', light: '#A89078', plank: '#7A6352' };
    const metal = isActive 
      ? { main: '#B8860B', light: '#DAA520' }
      : { main: '#6B7280', light: '#9CA3AF' };
    
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_12px_rgba(205,133,63,0.7)]' : ''}>
        <defs>
          <linearGradient id={`${id}-wood`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={woodColor.light} />
            <stop offset="50%" stopColor={woodColor.main} />
            <stop offset="100%" stopColor={woodColor.dark} />
          </linearGradient>
        </defs>
        
        {/* Shadow */}
        <ellipse cx="32" cy="60" rx="18" ry="3" fill="rgba(0,0,0,0.2)" />
        
        {/* Crate body */}
        <rect x="12" y="30" width="40" height="28" rx="2" fill={`url(#${id}-wood)`} />
        
        {/* Wood planks */}
        <rect x="12" y="30" width="40" height="7" fill={woodColor.light} opacity="0.4" />
        <line x1="12" y1="37" x2="52" y2="37" stroke={woodColor.plank} strokeWidth="2" />
        <line x1="12" y1="44" x2="52" y2="44" stroke={woodColor.plank} strokeWidth="1.5" />
        <line x1="12" y1="51" x2="52" y2="51" stroke={woodColor.plank} strokeWidth="1.5" />
        
        {/* Vertical wood strips */}
        <rect x="14" y="30" width="3" height="28" fill={woodColor.dark} opacity="0.3" />
        <rect x="47" y="30" width="3" height="28" fill={woodColor.dark} opacity="0.3" />
        
        {/* Metal corner brackets */}
        <path d="M12 30 L18 30 L18 36 L14 36 L14 34 L12 34 Z" fill={metal.main} />
        <path d="M52 30 L46 30 L46 36 L50 36 L50 34 L52 34 Z" fill={metal.main} />
        <path d="M12 58 L18 58 L18 52 L14 52 L14 54 L12 54 Z" fill={metal.main} />
        <path d="M52 58 L46 58 L46 52 L50 52 L50 54 L52 54 Z" fill={metal.main} />
        
        {/* Coins peeking from top */}
        <Coin x={26} y={28} r={5} tilt={-15} />
        <Coin x={38} y={27} r={5} tilt={10} />
        <Coin x={32} y={24} r={5.5} tilt={0} />
        
        {/* Center label/stamp */}
        <rect x="26" y="40" width="12" height="10" rx="1" fill={metal.main} />
        <circle cx="32" cy="45" r="3" fill={metal.light} />
        
        {claimed && <circle cx="50" cy="16" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M46 16 L49 19 L55 13" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 4: BRONZE/COPPER CHEST - slightly open, warm metallic tones
  if (day === 4) {
    const copper = claimed 
      ? { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', accent: '#86EFAC' }
      : current 
      ? { main: '#CD7F32', dark: '#8B5A2B', light: '#DDA15E', accent: '#E9C16A' }
      : { main: '#8B7355', dark: '#5C4A3D', light: '#A89078', accent: '#B8A090' };
    const trim = isActive 
      ? { main: '#B87333', light: '#DAA520' }
      : { main: '#6B7280', light: '#9CA3AF' };
    
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_14px_rgba(184,115,51,0.7)]' : ''}>
        <defs>
          <linearGradient id={`${id}-copper`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={copper.accent} />
            <stop offset="30%" stopColor={copper.light} />
            <stop offset="70%" stopColor={copper.main} />
            <stop offset="100%" stopColor={copper.dark} />
          </linearGradient>
          <linearGradient id={`${id}-copperLid`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={copper.accent} />
            <stop offset="50%" stopColor={copper.main} />
            <stop offset="100%" stopColor={copper.dark} />
          </linearGradient>
        </defs>
        
        {/* Shadow */}
        <ellipse cx="32" cy="60" rx="20" ry="3" fill="rgba(0,0,0,0.2)" />
        
        {/* Open Lid tilted back */}
        <g transform="translate(32, 26) rotate(-20) translate(-32, -26)">
          <path d="M10 26 Q10 14 32 12 Q54 14 54 26 L54 32 L10 32 Z" fill={`url(#${id}-copperLid)`} />
          <ellipse cx="32" cy="16" rx="18" ry="4" fill={trim.main} />
          <rect x="8" y="28" width="48" height="4" rx="1" fill={trim.main} />
          {/* Lid shine */}
          <ellipse cx="26" cy="20" rx="8" ry="3" fill="white" opacity="0.2" />
        </g>
        
        {/* Interior dark */}
        <rect x="12" y="26" width="40" height="10" fill="#1a1410" />
        
        {/* Coins visible */}
        <Coin x={22} y={32} r={4.5} tilt={-12} />
        <Coin x={32} y={31} r={5} tilt={0} />
        <Coin x={42} y={32} r={4.5} tilt={12} />
        <Coin x={27} y={26} r={5} tilt={-8} />
        <Coin x={37} y={26} r={5} tilt={8} />
        <Coin x={32} y={21} r={5.5} tilt={0} />
        <Gem x={32} y={16} r={4} />
        
        {/* Chest body - rounded edges */}
        <rect x="10" y="34" width="44" height="24" rx="4" fill={`url(#${id}-copper)`} />
        
        {/* Decorative rivets */}
        {[16, 26, 38, 48].map((x, i) => (
          <circle key={i} cx={x} cy="38" r="2" fill={trim.light} />
        ))}
        {[16, 26, 38, 48].map((x, i) => (
          <circle key={i} cx={x} cy="54" r="2" fill={trim.light} />
        ))}
        
        {/* Metal bands */}
        <rect x="8" y="32" width="48" height="4" rx="1" fill={trim.main} />
        <rect x="8" y="54" width="48" height="4" rx="1" fill={trim.main} />
        
        {/* Central emblem */}
        <circle cx="32" cy="46" r="6" fill={trim.main} />
        <circle cx="32" cy="46" r="4" fill={trim.light} />
        <circle cx="32" cy="46" r="2" fill={copper.dark} />
        
        {/* Shine highlight */}
        <ellipse cx="22" cy="40" rx="6" ry="3" fill="white" opacity="0.15" />
        
        {claimed && <circle cx="52" cy="10" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M48 10 L51 13 L57 7" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 5: SILVER/STEEL CHEST - larger, more open, shiny metallic
  if (day === 5) {
    const silver = claimed 
      ? { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', shine: '#DCFCE7' }
      : current 
      ? { main: '#A8A9AD', dark: '#71717A', light: '#D4D4D8', shine: '#F4F4F5' }
      : { main: '#78716C', dark: '#57534E', light: '#A8A29E', shine: '#D6D3D1' };
    const trim = isActive 
      ? { main: '#94A3B8', light: '#CBD5E1' }
      : { main: '#6B7280', light: '#9CA3AF' };
    
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_16px_rgba(148,163,184,0.8)]' : ''}>
        <defs>
          <linearGradient id={`${id}-silver`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={silver.shine} />
            <stop offset="30%" stopColor={silver.light} />
            <stop offset="70%" stopColor={silver.main} />
            <stop offset="100%" stopColor={silver.dark} />
          </linearGradient>
          <linearGradient id={`${id}-silverLid`} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={silver.dark} />
            <stop offset="50%" stopColor={silver.main} />
            <stop offset="100%" stopColor={silver.shine} />
          </linearGradient>
        </defs>
        
        {/* Shadow */}
        <ellipse cx="32" cy="62" rx="24" ry="3" fill="rgba(0,0,0,0.2)" />
        
        {/* Spilled coins */}
        <Coin x={6} y={58} r={3.5} tilt={-25} />
        <Coin x={58} y={56} r={3.5} tilt={20} />
        
        {/* Open Lid - more angled */}
        <g transform="translate(32, 24) rotate(-35) translate(-32, -24)">
          <path d="M8 24 Q8 10 32 8 Q56 10 56 24 L56 30 L8 30 Z" fill={`url(#${id}-silverLid)`} />
          <ellipse cx="32" cy="12" rx="20" ry="4" fill={trim.main} />
          <rect x="6" y="26" width="52" height="4" rx="1" fill={trim.main} />
          {/* Metal shine streaks */}
          <line x1="16" y1="16" x2="26" y2="14" stroke="white" strokeWidth="2" opacity="0.4" />
          <line x1="38" y1="14" x2="48" y2="16" stroke="white" strokeWidth="2" opacity="0.4" />
        </g>
        
        {/* Interior */}
        <rect x="10" y="22" width="44" height="14" fill="#1a1410" />
        
        {/* Bigger coin pile */}
        <Coin x={18} y={36} r={4.5} tilt={-20} />
        <Coin x={28} y={34} r={5} tilt={-8} />
        <Coin x={36} y={34} r={5} tilt={8} />
        <Coin x={46} y={36} r={4.5} tilt={20} />
        <Coin x={23} y={28} r={5} tilt={-12} />
        <Coin x={32} y={26} r={5.5} tilt={0} />
        <Coin x={41} y={28} r={5} tilt={12} />
        <Coin x={28} y={21} r={5.5} tilt={-5} />
        <Coin x={36} y={21} r={5.5} tilt={5} />
        <Coin x={32} y={15} r={6} tilt={0} />
        <Gem x={22} y={16} r={4} />
        <Gem x={42} y={18} r={3.5} />
        
        {/* Chest body */}
        <rect x="6" y="34" width="52" height="26" rx="4" fill={`url(#${id}-silver)`} />
        
        {/* Steel panel insets */}
        <rect x="10" y="38" width="20" height="18" rx="2" fill={silver.dark} opacity="0.3" />
        <rect x="34" y="38" width="20" height="18" rx="2" fill={silver.dark} opacity="0.3" />
        
        {/* Trim bands */}
        <rect x="4" y="32" width="56" height="4" rx="1" fill={trim.main} />
        <rect x="4" y="56" width="56" height="4" rx="1" fill={trim.main} />
        
        {/* Handle on front */}
        <rect x="28" y="44" width="8" height="3" rx="1" fill={trim.light} />
        <rect x="26" y="42" width="12" height="2" rx="1" fill={trim.main} />
        
        {/* Shine effects */}
        <ellipse cx="18" cy="40" rx="6" ry="3" fill="white" opacity="0.2" />
        <line x1="8" y1="36" x2="16" y2="36" stroke="white" strokeWidth="2" opacity="0.3" />
        
        {claimed && <circle cx="54" cy="8" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M50 8 L53 11 L59 5" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 6: GOLDEN ORNATE CHEST - fancy, overflowing
  if (day === 6) {
    const gold = claimed 
      ? { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', shine: '#DCFCE7' }
      : current 
      ? { main: '#DAA520', dark: '#B8860B', light: '#FFD700', shine: '#FEF3C7' }
      : { main: '#8B7355', dark: '#6B5344', light: '#A89078', shine: '#C4B8A8' };
    const ruby = isActive ? '#EF4444' : '#9CA3AF';
    
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_18px_rgba(218,165,32,0.85)]' : ''}>
        <defs>
          <linearGradient id={`${id}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gold.shine} />
            <stop offset="25%" stopColor={gold.light} />
            <stop offset="60%" stopColor={gold.main} />
            <stop offset="100%" stopColor={gold.dark} />
          </linearGradient>
          <linearGradient id={`${id}-goldLid`} x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={gold.dark} />
            <stop offset="40%" stopColor={gold.main} />
            <stop offset="100%" stopColor={gold.shine} />
          </linearGradient>
        </defs>
        
        {/* Shadow */}
        <ellipse cx="32" cy="62" rx="26" ry="3" fill="rgba(0,0,0,0.2)" />
        
        {/* Spilled treasures */}
        <Coin x={4} y={58} r={3.5} tilt={-30} />
        <Coin x={60} y={56} r={4} tilt={25} />
        <Coin x={8} y={50} r={3} tilt={-40} />
        <Gem x={56} y={48} r={3.5} />
        
        {/* Wide open Lid */}
        <g transform="translate(32, 22) rotate(-50) translate(-32, -22)">
          <path d="M6 22 Q6 6 32 4 Q58 6 58 22 L58 28 L6 28 Z" fill={`url(#${id}-goldLid)`} />
          <ellipse cx="32" cy="8" rx="22" ry="4" fill={gold.main} />
          <rect x="4" y="24" width="56" height="4" rx="1" fill={gold.main} />
          {/* Ornate top decoration */}
          <circle cx="32" cy="10" r="4" fill={ruby} />
          <circle cx="32" cy="10" r="2" fill="white" opacity="0.5" />
        </g>
        
        {/* Interior */}
        <rect x="8" y="20" width="48" height="16" fill="#1a1410" />
        
        {/* Massive overflowing coins */}
        <Coin x={16} y={38} r={4.5} tilt={-25} />
        <Coin x={24} y={36} r={5} tilt={-12} />
        <Coin x={32} y={34} r={5.5} tilt={0} />
        <Coin x={40} y={36} r={5} tilt={12} />
        <Coin x={48} y={38} r={4.5} tilt={25} />
        <Coin x={20} y={30} r={5} tilt={-18} />
        <Coin x={28} y={28} r={5.5} tilt={-6} />
        <Coin x={36} y={28} r={5.5} tilt={6} />
        <Coin x={44} y={30} r={5} tilt={18} />
        <Coin x={24} y={22} r={5.5} tilt={-10} />
        <Coin x={32} y={20} r={6} tilt={0} />
        <Coin x={40} y={22} r={5.5} tilt={10} />
        <Coin x={28} y={14} r={5.5} tilt={-5} />
        <Coin x={36} y={14} r={5.5} tilt={5} />
        <Coin x={32} y={8} r={6} tilt={0} />
        <Gem x={20} y={16} r={4.5} />
        <Gem x={44} y={18} r={4} />
        <Gem x={32} y={2} r={4.5} />
        
        {/* Ornate chest body */}
        <rect x="4" y="34" width="56" height="26" rx="4" fill={`url(#${id}-gold)`} />
        
        {/* Decorative embossing */}
        <rect x="8" y="38" width="48" height="18" rx="3" fill={gold.dark} opacity="0.2" />
        
        {/* Corner jewels */}
        <circle cx="12" cy="42" r="3" fill={ruby} />
        <circle cx="52" cy="42" r="3" fill={ruby} />
        <circle cx="12" cy="52" r="3" fill="#3B82F6" />
        <circle cx="52" cy="52" r="3" fill="#3B82F6" />
        
        {/* Ornate trim */}
        <rect x="2" y="32" width="60" height="4" rx="1" fill={gold.main} />
        <rect x="2" y="56" width="60" height="4" rx="1" fill={gold.main} />
        
        {/* Central medallion */}
        <circle cx="32" cy="47" r="7" fill={gold.main} stroke={gold.dark} strokeWidth="2" />
        <circle cx="32" cy="47" r="4" fill={ruby} />
        <circle cx="30" cy="45" r="1.5" fill="white" opacity="0.6" />
        
        {/* Sparkle effects */}
        {isActive && (
          <>
            <circle cx="8" cy="30" r="2" fill="#FEF08A" className="animate-pulse" />
            <circle cx="56" cy="28" r="2" fill="#FEF08A" className="animate-pulse" style={{ animationDelay: '300ms' }} />
          </>
        )}
        
        {claimed && <circle cx="56" cy="6" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
        {claimed && <path d="M52 6 L55 9 L61 3" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
      </svg>
    );
  }

  // Day 7: ROYAL TREASURE CHEST - massive, purple & gold, JACKPOT explosion!
  const royal = claimed 
    ? { main: '#22C55E', dark: '#16A34A', light: '#4ADE80', accent: '#86EFAC' }
    : current 
    ? { main: '#7C3AED', dark: '#5B21B6', light: '#A78BFA', accent: '#DDD6FE' }
    : { main: '#6B5B7A', dark: '#4C4058', light: '#8B7B9A', accent: '#A898B8' };
  const royalGold = isActive 
    ? { main: '#FFD700', dark: '#B8860B', light: '#FEF3C7' }
    : { main: '#9CA3AF', dark: '#6B7280', light: '#D1D5DB' };
  
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={isActive ? 'drop-shadow-[0_0_24px_rgba(124,58,237,0.9)]' : ''}>
      <defs>
        <linearGradient id={`${id}-royal`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={royal.accent} />
          <stop offset="30%" stopColor={royal.light} />
          <stop offset="70%" stopColor={royal.main} />
          <stop offset="100%" stopColor={royal.dark} />
        </linearGradient>
        <linearGradient id={`${id}-royalLid`} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={royal.dark} />
          <stop offset="40%" stopColor={royal.main} />
          <stop offset="100%" stopColor={royal.accent} />
        </linearGradient>
      </defs>
      
      {/* Glow effect for jackpot */}
      {isActive && (
        <ellipse cx="32" cy="40" rx="32" ry="28" fill={current ? '#A78BFA' : '#4ADE80'} opacity="0.15" />
      )}
      
      {/* Shadow */}
      <ellipse cx="32" cy="63" rx="28" ry="3" fill="rgba(0,0,0,0.25)" />
      
      {/* Scattered treasure EVERYWHERE */}
      <Coin x={2} y={58} r={4} tilt={-35} />
      <Coin x={62} y={56} r={4.5} tilt={30} />
      <Coin x={6} y={48} r={3.5} tilt={-45} />
      <Coin x={58} y={46} r={3.5} tilt={40} />
      <Gem x={10} y={52} r={4} />
      <Gem x={54} y={50} r={3.5} />
      
      {/* Fully open magnificent lid */}
      <g transform="translate(32, 20) rotate(-60) translate(-32, -20)">
        <path d="M4 20 Q4 2 32 0 Q60 2 60 20 L60 26 L4 26 Z" fill={`url(#${id}-royalLid)`} />
        <ellipse cx="32" cy="4" rx="24" ry="4" fill={royalGold.main} />
        <rect x="2" y="22" width="60" height="4" rx="1" fill={royalGold.main} />
        {/* Crown on lid */}
        <path d="M24 6 L26 2 L30 4 L32 0 L34 4 L38 2 L40 6 Z" fill={royalGold.main} stroke={royalGold.dark} strokeWidth="0.5" />
        <circle cx="32" cy="4" r="2" fill="#EF4444" />
      </g>
      
      {/* Interior */}
      <rect x="6" y="18" width="52" height="18" fill="#1a1410" />
      
      {/* EXPLOSION of coins */}
      <Coin x={14} y={40} r={4.5} tilt={-30} />
      <Coin x={22} y={38} r={5} tilt={-18} />
      <Coin x={32} y={36} r={5.5} tilt={0} />
      <Coin x={42} y={38} r={5} tilt={18} />
      <Coin x={50} y={40} r={4.5} tilt={30} />
      <Coin x={18} y={32} r={5.5} tilt={-22} />
      <Coin x={26} y={30} r={5.5} tilt={-10} />
      <Coin x={38} y={30} r={5.5} tilt={10} />
      <Coin x={46} y={32} r={5.5} tilt={22} />
      <Coin x={22} y={24} r={6} tilt={-15} />
      <Coin x={32} y={22} r={6.5} tilt={0} />
      <Coin x={42} y={24} r={6} tilt={15} />
      <Coin x={26} y={16} r={6} tilt={-8} />
      <Coin x={38} y={16} r={6} tilt={8} />
      <Coin x={32} y={10} r={6.5} tilt={0} />
      {/* Flying coins */}
      <Coin x={20} y={6} r={5.5} tilt={-25} />
      <Coin x={44} y={4} r={5.5} tilt={25} />
      <Coin x={32} y={2} r={6} tilt={0} />
      <Coin x={14} y={10} r={5} tilt={-35} />
      <Coin x={50} y={8} r={5} tilt={35} />
      {/* Gems scattered */}
      <Gem x={16} y={18} r={5} />
      <Gem x={48} y={20} r={4.5} />
      <Gem x={24} y={4} r={4.5} />
      <Gem x={40} y={2} r={4.5} />
      <Gem x={32} y={-4} r={5} />
      <Gem x={8} y={16} r={4} />
      <Gem x={56} y={14} r={4} />
      
      {/* Magnificent chest body */}
      <rect x="2" y="34" width="60" height="28" rx="4" fill={`url(#${id}-royal)`} />
      
      {/* Ornate gold frame */}
      <rect x="6" y="38" width="52" height="20" rx="3" stroke={royalGold.main} strokeWidth="2" fill="none" />
      
      {/* Royal trim bands */}
      <rect x="0" y="32" width="64" height="4" rx="1" fill={royalGold.main} />
      <rect x="0" y="58" width="64" height="4" rx="1" fill={royalGold.main} />
      
      {/* Corner jewels */}
      <circle cx="10" cy="40" r="3.5" fill="#EF4444" />
      <circle cx="54" cy="40" r="3.5" fill="#EF4444" />
      <circle cx="10" cy="54" r="3.5" fill="#3B82F6" />
      <circle cx="54" cy="54" r="3.5" fill="#3B82F6" />
      
      {/* Central royal crest */}
      <ellipse cx="32" cy="48" rx="10" ry="8" fill={royalGold.main} stroke={royalGold.dark} strokeWidth="2" />
      <text x="32" y="52" textAnchor="middle" fill={royal.dark} fontWeight="900" fontSize="10" fontFamily="Arial Black">7</text>
      
      {/* Crown on top */}
      {isActive && (
        <g transform="translate(32, -12)">
          <path d="M-14 16 L-11 4 L-6 10 L0 0 L6 10 L11 4 L14 16 Z" fill={royalGold.main} stroke={royalGold.dark} strokeWidth="1.5" />
          <circle cx="-6" cy="10" r="2.5" fill="#EF4444" />
          <circle cx="0" cy="6" r="3" fill="#3B82F6" />
          <circle cx="6" cy="10" r="2.5" fill="#22C55E" />
          {/* Crown shine */}
          <ellipse cx="-8" cy="8" rx="3" ry="1.5" fill="white" opacity="0.4" />
        </g>
      )}
      
      {/* Sparkle effects */}
      {isActive && (
        <>
          <circle cx="4" cy="16" r="2.5" fill="#FEF08A" className="animate-pulse" />
          <circle cx="60" cy="14" r="3" fill="#FEF08A" className="animate-pulse" style={{ animationDelay: '200ms' }} />
          <circle cx="0" cy="32" r="2.5" fill="#FEF08A" className="animate-pulse" style={{ animationDelay: '400ms' }} />
          <circle cx="64" cy="30" r="2.5" fill="#FEF08A" className="animate-pulse" style={{ animationDelay: '600ms' }} />
          <circle cx="32" cy="-8" r="3" fill="#FEF08A" className="animate-pulse" style={{ animationDelay: '100ms' }} />
        </>
      )}
      
      {claimed && <circle cx="58" cy="0" r="8" fill="#22C55E" stroke="white" strokeWidth="2" />}
      {claimed && <path d="M54 0 L57 3 L63 -3" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />}
    </svg>
  );
};

// Fire/flame icon for streak
const FlameIcon = ({ size = 40, hot = false, mega = false }: { size?: number; hot?: boolean; mega?: boolean }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" className="drop-shadow-[0_2px_8px_rgba(255,100,0,0.6)]">
    <path 
      d="M20 2C20 2 10 12 10 22C10 30 14 36 20 36C26 36 30 30 30 22C30 12 20 2 20 2Z"
      fill={mega ? 'url(#megaFlame)' : hot ? 'url(#hotFlame)' : 'url(#normalFlame)'}
    />
    <path 
      d="M20 10C20 10 15 18 15 25C15 30 17 33 20 33C23 33 25 30 25 25C25 18 20 10 20 10Z"
      fill={mega ? '#F472B6' : hot ? '#FEF08A' : '#FEF9C3'}
    />
    <ellipse cx="17" cy="22" rx="2" ry="3" fill="rgba(255,255,255,0.6)" />
    <defs>
      <linearGradient id="normalFlame" x1="20" y1="2" x2="20" y2="36">
        <stop offset="0%" stopColor="#FF6B00" />
        <stop offset="100%" stopColor="#DC2626" />
      </linearGradient>
      <linearGradient id="hotFlame" x1="20" y1="2" x2="20" y2="36">
        <stop offset="0%" stopColor="#FFD700" />
        <stop offset="100%" stopColor="#FF6B00" />
      </linearGradient>
      <linearGradient id="megaFlame" x1="20" y1="2" x2="20" y2="36">
        <stop offset="0%" stopColor="#E879F9" />
        <stop offset="100%" stopColor="#8B5CF6" />
      </linearGradient>
    </defs>
  </svg>
);

export const DailyBonusModal = ({ isOpen, onClose }: DailyBonusModalProps) => {
  const { toast } = useToast();
  const { status, loading, error, refetch } = useDailyBonusStatus();
  const { refetch: refetchContext, streakBrokenInfo, clearStreakBrokenInfo } = useDailyBonus();
  const { claim, claiming, claimResult, reset } = useClaimDailyBonus();
  const { checkVerificationAndPrompt } = useVerification();
  const { userVipStatus } = useVip();
  const [claimSuccess, setClaimSuccess] = useState(false);

  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Swipe to close handlers - only from top drag handle area
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    
    // Only allow downward dragging
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    const threshold = 100; // Close if dragged down more than 100px
    if (dragY > threshold) {
      onClose();
    } else {
      // Animate back to original position
      setDragY(0);
    }
    
    // Reset
    setIsDragging(false);
    dragStartY.current = 0;
  };

  // Reset drag state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  // Prevent pull-to-refresh when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalOverscrollBehavior = document.body.style.overscrollBehaviorY;
      const originalOverscrollBehaviorHtml = document.documentElement.style.overscrollBehaviorY;
      
      document.body.style.overscrollBehaviorY = 'none';
      document.documentElement.style.overscrollBehaviorY = 'none';
      
      return () => {
        document.body.style.overscrollBehaviorY = originalOverscrollBehavior;
        document.documentElement.style.overscrollBehaviorY = originalOverscrollBehaviorHtml;
      };
    }
  }, [isOpen]);

  // Get tier color and level from VIP status
  const tierColor = userVipStatus?.tier_color || '#F59E0B';
  const tierLevel = userVipStatus?.tier_level || 1;
  
  // Get tier icon based on level
  const getTierIcon = (level: number) => {
    switch (level) {
      case 1: return Shield;
      case 2: return Award;
      case 3: return Star;
      case 4: return Crown;
      case 5: return Diamond;
      case 6: return Gem;
      case 7: return Diamond;
      case 8: return Crown;
      default: return Shield;
    }
  };
  const TierIcon = getTierIcon(tierLevel);

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setClaimSuccess(false);
      reset();
      if (streakBrokenInfo) {
        clearStreakBrokenInfo();
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset, streakBrokenInfo, clearStreakBrokenInfo]);

  useEffect(() => {
    if (claimResult?.success) {
      setClaimSuccess(true);
      toast({
        title: 'Daily Bonus Claimed!',
        description: `You received ${claimResult.reward?.gc?.toLocaleString() ?? 0} GC${claimResult.reward?.sc ? ` + ${claimResult.reward.sc} SC` : ''}!`,
        variant: 'success',
      });
      refetchContext();
      refetch();
    } else if (claimResult && !claimResult.success && claimResult.message) {
      toast({
        title: 'Claim Failed',
        description: claimResult.message,
        variant: 'destructive',
      });
    }
  }, [claimResult, toast, refetchContext, refetch]);

  const handleClaim = async () => {
    if (!checkVerificationAndPrompt('data')) {
      onClose();
      return;
    }
    await claim();
  };

  const handleClose = () => {
    onClose();
  };

  // Full-screen on mobile/tablet, centered modal on desktop (matching RedeemModal pattern)
  // Desktop: use max-h-[90vh] to fit content without scrolling when possible
  const fullScreenDialogClass = "!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-xl lg:!h-auto lg:!max-h-[90vh] m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-amber-500/20 bg-transparent overflow-hidden !translate-x-0 translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] flex flex-col [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 pb-[env(safe-area-inset-bottom)] lg:pb-0 lg:shadow-[0_0_60px_rgba(251,191,36,0.1),0_25px_50px_rgba(0,0,0,0.15)]";

  // Loading state
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={fullScreenDialogClass} aria-describedby={undefined}>
          {/* Drag Handle - Mobile & Tablet Only */}
          <div 
            className="lg:hidden pt-3 md:pt-4 pb-2 md:pb-3 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent"
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
          >
            <div className="w-10 md:w-14 h-1.5 bg-foreground/20 rounded-full" />
          </div>
          {/* Close button */}
          <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] md:right-5 md:top-[calc(env(safe-area-inset-top)+1.25rem)] lg:right-4 lg:top-4 z-20">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="flex h-9 w-9 md:h-11 md:w-11 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 md:h-5 md:w-5 lg:h-5 lg:w-5 text-muted-foreground" />
            </Button>
          </div>
          <div className="w-full flex-1 bg-card flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="h-16 w-16 text-amber-500 animate-spin drop-shadow-lg" />
              <span className="text-foreground/80 mt-4 text-lg font-semibold">Loading your rewards...</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (error || !status?.enabled) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={fullScreenDialogClass} aria-describedby={undefined}>
          {/* Drag Handle - Mobile & Tablet Only */}
          <div 
            className="lg:hidden pt-3 md:pt-4 pb-2 md:pb-3 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent"
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
          >
            <div className="w-10 md:w-14 h-1.5 bg-foreground/20 rounded-full" />
          </div>
          {/* Close button */}
          <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] md:right-5 md:top-[calc(env(safe-area-inset-top)+1.25rem)] lg:right-4 lg:top-4 z-20">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="flex h-9 w-9 md:h-11 md:w-11 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 md:h-5 md:w-5 lg:h-5 lg:w-5 text-muted-foreground" />
            </Button>
          </div>
          <div className="w-full flex-1 bg-card flex items-center justify-center">
            <div className="text-center px-6">
              <Gift className="h-24 w-24 text-amber-400/50 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-foreground mb-2">Daily Bonus Unavailable</h3>
              <p className="text-muted-foreground mb-6">Please try again later.</p>
              <Button onClick={handleClose} className="bg-amber-500 text-white hover:bg-amber-400 font-bold px-8">Close</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const canClaim = status.todayReward?.canClaim ?? false;
  const vipMultiplier = status.vipTier.multiplier;
  const hasVipBoost = vipMultiplier > 1;
  const isHotStreak = status.streak.currentStreak >= 7;
  const isMegaStreak = status.streak.currentStreak >= 14;

  // Calculate base amounts (before VIP multiplier) for Today's Reward display
  // This lets us show the "crossed off" original amount to emphasize VIP value
  const todayBaseGC = hasVipBoost ? Math.round(status.todayReward.gc / vipMultiplier) : status.todayReward.gc;
  const todayBaseSC = hasVipBoost ? Number((status.todayReward.sc / vipMultiplier).toFixed(2)) : status.todayReward.sc;
  const todayBonusGC = status.todayReward.gc - todayBaseGC;
  const todayBonusSC = Number((status.todayReward.sc - todayBaseSC).toFixed(2));

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        ref={modalContentRef}
        className={fullScreenDialogClass}
        style={{
          '--tw-translate-y': dragY > 0 ? `${dragY}px` : '0px',
          opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 400) : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overscrollBehaviorY: 'none',
        } as React.CSSProperties}
        aria-describedby={undefined}
      >
        {/* Drag Handle Indicator - Mobile & Tablet Only */}
        <div 
          className="lg:hidden pt-[calc(env(safe-area-inset-top)+0.75rem)] md:pt-[calc(env(safe-area-inset-top)+1rem)] pb-2 md:pb-3 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent absolute top-0 left-0 right-0 z-30"
          onTouchStart={handleDragHandleTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
        >
          <div 
            className="w-10 md:w-14 h-1.5 bg-foreground/20 rounded-full transition-all duration-200"
            style={{
              opacity: isDragging ? 0.6 : 1,
              transform: isDragging ? 'scaleX(0.6)' : 'scaleX(1)',
              backgroundColor: isDragging ? 'rgba(128, 128, 128, 0.7)' : undefined,
              transition: isDragging ? 'none' : 'all 0.2s ease',
            }}
          />
        </div>

        {/* Close button - wrapped in div to avoid [&>button]:hidden selector */}
        <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] md:right-5 md:top-[calc(env(safe-area-inset-top)+1.25rem)] lg:right-4 lg:top-4 z-50">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="flex h-9 w-9 md:h-11 md:w-11 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
            aria-label="Close"
          >
            <X className="h-4 w-4 md:h-5 md:w-5 lg:h-5 lg:w-5 text-muted-foreground hover:text-foreground transition-colors" />
          </Button>
        </div>

        {/* PREMIUM GRADIENT BACKGROUND */}
        <div className="w-full h-full lg:h-auto bg-gradient-to-b from-card via-card to-card overflow-hidden relative flex-1 flex flex-col">

          {/* Radial glow accents */}
          <RadialGlow />
          
          {/* Floating GC and SC coins */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <FloatingCoin className="top-[8%] left-[8%]" size="lg" delay={0} type="gc" />
            <FloatingCoin className="top-[12%] right-[12%]" size="md" delay={400} type="sc" />
            <FloatingCoin className="top-[25%] left-[5%]" size="sm" delay={800} type="gc" />
            <FloatingCoin className="top-[20%] right-[8%]" size="lg" delay={1200} type="gc" />
            <FloatingCoin className="bottom-[25%] left-[10%]" size="md" delay={600} type="sc" />
            <FloatingCoin className="bottom-[20%] right-[6%]" size="md" delay={1000} type="gc" />
            <FloatingCoin className="bottom-[35%] left-[3%]" size="sm" delay={200} type="sc" />
            <FloatingCoin className="bottom-[15%] right-[15%]" size="sm" delay={1400} type="gc" />
          </div>

          {/* Sparkle stars */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <Star className="top-[5%] left-[20%]" size={20} delay={100} />
            <Star className="top-[10%] right-[25%]" size={28} delay={300} />
            <Star className="top-[15%] left-[35%]" size={16} delay={600} />
            <Star className="top-[8%] right-[40%]" size={22} delay={900} />
            <Star className="bottom-[30%] left-[20%]" size={18} delay={500} />
            <Star className="bottom-[25%] right-[30%]" size={24} delay={800} />
            <Star className="top-[30%] right-[5%]" size={14} delay={1100} />
            <Star className="top-[35%] left-[8%]" size={20} delay={700} />
          </div>

          {/* Content container - scrollable on mobile, fits content on desktop when possible */}
          <div className="relative w-full flex-1 min-h-0 flex flex-col items-center px-2 min-[390px]:px-3 sm:px-4 pt-12 lg:pt-6 pb-4 lg:pb-5 overflow-y-auto lg:overflow-y-visible overflow-x-visible overscroll-contain">
            {/* Header with stars */}
            <div className="text-center mb-2 lg:mb-3 relative px-4 sm:px-8">
              <div className="relative inline-block overflow-visible">
                {/* Decorative stars around title */}
                <Star className="-top-2 -left-6 sm:-left-14 hidden min-[390px]:block" size={16} delay={0} />
                <Star className="-top-1 -right-8 sm:-right-16 hidden min-[390px]:block" size={20} delay={200} />
                <Star className="top-6 -left-5 sm:-left-12 hidden sm:block" size={18} delay={400} />
                <Star className="top-5 -right-4 sm:-right-10 hidden sm:block" size={20} delay={600} />
                
                <h1 className="text-[clamp(1.125rem,4vw,2.25rem)] font-black tracking-tight bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(251,191,36,0.3)] whitespace-nowrap">
                  DAILY BONUS
                </h1>
              </div>
              <p className="text-[clamp(0.625rem,2vw,1rem)] text-amber-600 mt-0.5 font-semibold">
                Collect rewards every day!
              </p>
            </div>

            {/* Stats Row - Streak + Multiplier side by side */}
            <div className="flex flex-wrap items-stretch justify-center gap-3 sm:gap-4 mb-2 lg:mb-3">
              {/* Streak Counter Card */}
              <div className={`
                flex items-center justify-center gap-3 px-4 py-1.5 rounded-xl backdrop-blur-sm min-w-[140px] w-[160px]
                ${isMegaStreak
                  ? 'bg-purple-500/20 border-2 border-purple-400 shadow-[0_0_25px_rgba(168,85,247,0.3)]'
                  : isHotStreak
                    ? 'bg-orange-500/20 border-2 border-orange-400 shadow-[0_0_25px_rgba(255,150,0,0.3)]'
                    : 'bg-amber-500/10 border border-amber-400/30 shadow-lg'
                }
              `}>
                <FlameIcon size={28} hot={isHotStreak} mega={isMegaStreak} />
                <div className="flex flex-col items-start">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Streak</span>
                  <div className="flex items-baseline gap-1">
                    <span className="text-lg font-black text-foreground drop-shadow-lg">
                      {status.streak.currentStreak}
                    </span>
                    <span className="text-[11px] text-muted-foreground/70 font-semibold">days</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground/60 font-medium">Best: {status.streak.longestStreak}</span>
                </div>
              </div>
              
              {/* VIP Multiplier Card */}
              <div 
                className="flex items-center justify-center gap-3 px-4 py-1.5 rounded-xl backdrop-blur-sm min-w-[140px] w-[160px]"
                style={hasVipBoost ? {
                  background: `linear-gradient(135deg, ${tierColor}30 0%, ${tierColor}15 100%)`,
                  border: `2px solid ${tierColor}`,
                  boxShadow: `0 0 25px ${tierColor}30`,
                } : {
                  background: 'hsl(var(--muted) / 0.5)',
                  border: '1px solid hsl(var(--border))',
                  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.06)',
                }}
              >
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={hasVipBoost ? {
                    background: `linear-gradient(135deg, ${tierColor}, ${tierColor}80)`,
                    boxShadow: `0 2px 8px ${tierColor}50`,
                  } : {
                    background: 'hsl(var(--muted))',
                  }}
                >
                  <TierIcon className={`h-4 w-4 drop-shadow-md ${hasVipBoost ? 'text-white' : 'text-foreground'}`} />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Multiplier</span>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-lg font-black drop-shadow-lg ${
                      hasVipBoost ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {vipMultiplier}x
                    </span>
                  </div>
                  <span className={`text-[10px] font-medium ${hasVipBoost ? 'text-foreground/70' : 'text-muted-foreground/60'}`}>
                    {hasVipBoost ? `VIP ${status.vipTier.name}` : 'Base Rate'}
                  </span>
                </div>
              </div>
            </div>

            {/* Streak Broken Message */}
            {streakBrokenInfo?.streakBroken && streakBrokenInfo.previousStreak && (
              <div className="flex items-center gap-1.5 py-1.5 px-3 mb-2 lg:mb-3 bg-red-500/10 backdrop-blur-sm border border-red-400/30 rounded-xl max-w-md w-full">
                <HeartCrack className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div>
                  <span className="text-red-600 font-bold text-[clamp(0.625rem,2vw,0.875rem)]">Streak Reset!</span>
                  <p className="text-[clamp(0.5rem,1.5vw,0.75rem)] text-red-500/70">
                    Your {streakBrokenInfo.previousStreak}-day streak ended. Start fresh!
                  </p>
                </div>
              </div>
            )}

            {/* 7-Day Progress Section */}
            <div className="w-full max-w-2xl mb-2 lg:mb-3 bg-amber-500/[0.07] backdrop-blur-sm rounded-xl p-[1.5vw] sm:p-3 border border-amber-400/30">
              <div className="flex items-center justify-between mb-1 lg:mb-1.5">
                <span className="text-[clamp(0.5rem,1.8vw,0.75rem)] font-bold text-amber-700 uppercase tracking-wider">
                  7-Day Rewards Cycle
                </span>
                <span className="text-[clamp(0.5rem,1.8vw,0.75rem)] text-amber-600 font-bold bg-amber-500/15 px-1.5 py-0.5 rounded-full border border-amber-400/30">
                  Day {status.streak.currentCycleDay} of 7
                </span>
              </div>

              {/* Progress Bar */}
              <div className="relative h-[0.4vh] min-h-[3px] bg-foreground/10 rounded-full mb-2 lg:mb-2.5 overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 via-emerald-400 to-green-500 rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_rgba(34,197,94,0.5)]"
                  style={{ width: `${Math.max(5, ((status.streak.currentCycleDay - (canClaim ? 1 : 0)) / 7) * 100)}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/40 to-white/0 animate-shimmer" />
                </div>
              </div>

              {/* Day Cards Grid - 3 cols, Day 7 full width on row 3 */}
              <div className="grid grid-cols-3 gap-[1vw] sm:gap-2">
                {status.cycleProgress.map((day) => (
                  <div 
                    key={day.day}
                    className={day.day === 7 ? 'col-span-3' : ''}
                  >
                    <DayCard 
                      day={day} 
                      vipMultiplier={vipMultiplier}
                      hasVipBoost={hasVipBoost}
                      isFullWidth={day.day === 7}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Today's Reward / Countdown Section */}
            <div className="w-full max-w-md">
              {canClaim && !claimSuccess ? (
                <>
                  {/* Today's Reward Card */}
                  <div className="relative mb-2 lg:mb-3 p-[2vw] sm:p-4 bg-gradient-to-br from-amber-500/20 to-orange-600/10 backdrop-blur-sm border-2 border-amber-400/50 rounded-xl overflow-hidden shadow-[0_0_30px_rgba(251,191,36,0.2)]">
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-400/0 via-amber-400/10 to-amber-400/0 animate-shimmer" />
                    <div className="relative text-center">
                      <span className="text-[clamp(0.5rem,1.8vw,0.75rem)] font-bold text-amber-600 uppercase tracking-wider">Today's Reward</span>
                      <div className="flex items-center justify-center gap-[2vw] sm:gap-4 mt-1">
                        {/* GC Reward */}
                        <div className="flex items-center gap-2">
                          <GCBadgeSVG size={32} />
                          <div className="flex flex-col items-start">
                            <div className="flex items-center gap-2">
                              {/* Show crossed-off base amount for VIP users */}
                              {hasVipBoost && (
                                <span className="text-[clamp(0.75rem,2.5vw,1rem)] font-bold text-foreground/30 line-through decoration-foreground/40">
                                  {todayBaseGC.toLocaleString()}
                                </span>
                              )}
                              <span className="text-[clamp(1.125rem,4vw,1.5rem)] font-black text-foreground drop-shadow-lg">
                                {status.todayReward.gc.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        {status.todayReward.sc > 0 && (
                          <>
                            <span className="text-[clamp(1rem,3vw,1.25rem)] text-foreground/30 font-light">+</span>
                            {/* SC Reward */}
                            <div className="flex items-center gap-2">
                              <SCBadgeSVG size={32} />
                              <div className="flex flex-col items-start">
                                <div className="flex items-center gap-2">
                                  {/* Show crossed-off base amount for VIP users */}
                                  {hasVipBoost && todayBaseSC > 0 && (
                                    <span className="text-[clamp(0.75rem,2.5vw,1rem)] font-bold text-emerald-600/40 line-through decoration-emerald-600/50">
                                      {todayBaseSC}
                                    </span>
                                  )}
                                  <span className="text-[clamp(1.125rem,4vw,1.5rem)] font-black text-emerald-600 drop-shadow-lg">
                                    {status.todayReward.sc}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Claim Button */}
                  <Button
                    onClick={handleClaim}
                    disabled={claiming}
                    className="w-full h-11 lg:h-12 bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 hover:from-green-400 hover:via-emerald-400 hover:to-green-500 text-white text-[clamp(0.75rem,2.5vw,1rem)] font-black shadow-[0_8px_30px_rgba(34,197,94,0.5)] hover:shadow-[0_8px_40px_rgba(34,197,94,0.7)] transition-all rounded-xl relative overflow-hidden group border-2 border-green-300"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    {claiming ? (
                      <span className="relative flex items-center gap-1.5">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Claiming...
                      </span>
                    ) : (
                      <span className="relative">
                        CLAIM YOUR REWARD
                      </span>
                    )}
                  </Button>
                </>
              ) : claimSuccess && claimResult?.success ? (
                /* Success State - matches Today's Reward Card + Button height */
                (() => {
                  // Calculate bonus from actual claimed amount
                  const claimedGC = claimResult.reward?.gc ?? 0;
                  const claimedSC = claimResult.reward?.sc ?? 0;
                  const claimedBaseGC = hasVipBoost ? Math.round(claimedGC / vipMultiplier) : claimedGC;
                  const claimedBonusGC = claimedGC - claimedBaseGC;
                  
                  return (
                    <div className="relative p-[2vw] sm:p-4 bg-green-500/10 backdrop-blur-sm border-2 border-green-400/50 rounded-xl text-center overflow-hidden shadow-[0_0_30px_rgba(34,197,94,0.1)]">
                      <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-[0_0_30px_rgba(34,197,94,0.4)]">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-black text-green-600 mb-1">Claimed!</h3>
                      <p className="text-base text-foreground font-bold">
                        {claimedGC.toLocaleString()} GC
                        {claimedSC > 0 ? ` + ${claimedSC} SC` : ''}
                      </p>
                      {/* VIP Bonus Message - removed (math was incorrect) */}
                      <p className="text-xs text-muted-foreground mt-1">Come back tomorrow for more rewards!</p>
                    </div>
                  );
                })()
              ) : (
                /* Countdown State */
                <div className="bg-amber-500/5 backdrop-blur-sm rounded-xl p-[2vw] sm:p-3 border border-amber-400/20">
                  <CountdownTimer canClaimNow={false} />
                </div>
              )}
            </div>

            {/* VIP Upsell */}
            {!status.vipTier.isMaxTier && (
              <p className="text-center text-[clamp(0.5rem,1.5vw,0.625rem)] text-muted-foreground/60 mt-2 lg:mt-3 max-w-md font-medium">
                Upgrade your VIP tier for up to <span className="text-amber-600 font-bold">{status.vipTier.maxMultiplier}x</span> daily bonus rewards
              </p>
            )}

          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Day Card Component
interface DayCardProps {
  day: CycleProgressDay;
  vipMultiplier: number;
  hasVipBoost: boolean;
  isFullWidth?: boolean; // For Day 7 on mobile - renders horizontally
}

const DayCard = ({ day, vipMultiplier, hasVipBoost, isFullWidth = false }: DayCardProps) => {
  const isCurrent = day.status === 'current';
  const isEarned = day.status === 'earned';
  const isClaimed = day.status === 'claimed';
  const isUpcoming = day.status === 'upcoming';
  const isExpired = day.status === 'expired';
  const isActiveDay = isCurrent || isEarned;
  const isDay7 = day.day === 7;

  // day.gc and day.sc are BASE values from backend cycleProgress
  // For claimed, active, and upcoming days with VIP boost, calculate the multiplied values
  const shouldApplyVip = hasVipBoost && (isClaimed || isActiveDay || isUpcoming);
  
  // Calculate final amounts with VIP multiplier applied
  const finalGC = shouldApplyVip ? Math.floor(day.gc * vipMultiplier) : day.gc;
  const finalSC = shouldApplyVip ? Number((day.sc * vipMultiplier).toFixed(2)) : day.sc;
  
  // Calculate bonus (extra amount from VIP)
  const bonusGC = finalGC - day.gc;
  const bonusSC = finalSC - day.sc;
  const showGCBonus = shouldApplyVip && bonusGC > 0;
  const showSCBonus = shouldApplyVip && bonusSC > 0;
  
  // Check if we have SC to display (using final value for active/claimed days)
  const hasSC = finalSC > 0;

  // Format GC as whole number with commas
  const formatGC = (amount: number): string => {
    return Math.floor(amount).toLocaleString('en-US');
  };

  // Format SC with 2 decimal places
  const formatSC = (amount: number): string => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Day 7 full-width centered layout - special "jackpot" day
  if (isFullWidth) {
    return (
      <div
        className={`
          relative flex flex-col items-center justify-center rounded-xl py-2 px-3 transition-all h-full
          ${isClaimed ? 'bg-gradient-to-br from-green-600/80 to-emerald-700/80 ring-2 ring-green-400 shadow-[0_0_20px_rgba(34,197,94,0.4)]' : ''}
          ${isActiveDay ? 'bg-gradient-to-br from-amber-500/80 to-orange-600/80 ring-2 ring-amber-300 shadow-[0_0_25px_rgba(251,191,36,0.5)]' : ''}
          ${isUpcoming ? 'bg-muted/50 border border-border' : ''}
          ${isExpired ? 'bg-muted/30 opacity-50' : ''}
        `}
      >
        {/* Day Label */}
        <span className={`text-[10px] font-bold uppercase leading-none mb-1 ${
          isClaimed ? 'text-green-100' :
          isActiveDay ? 'text-yellow-100' :
          'text-muted-foreground'
        }`}>
          Day 7+
        </span>

        {/* Rewards - horizontal layout */}
        <div className="flex items-center justify-center gap-3">
          {/* GC Reward */}
          <div className={`flex items-center gap-1.5 ${isUpcoming || isExpired ? 'opacity-70' : ''}`}>
            <GCBadgeSVG size={26} className={isClaimed ? 'opacity-90' : isActiveDay ? '' : 'opacity-70'} />
            {/* Crossed-off base + final amount inline, same size */}
            {showGCBonus && (
              <span className="text-lg font-black text-foreground/30 line-through decoration-foreground/40 decoration-2 leading-none">
                {formatGC(day.gc)}
              </span>
            )}
            <span className={`text-lg font-black leading-none ${
              isClaimed ? 'text-white' :
              isActiveDay ? 'text-amber-200' :
              'text-foreground/70'
            }`}>
              {formatGC(finalGC)}
            </span>
          </div>
          {/* SC Reward */}
          {hasSC && (
            <div className={`flex items-center gap-1.5 ${isUpcoming || isExpired ? 'opacity-70' : ''}`}>
              <SCBadgeSVG size={26} className={isClaimed ? 'opacity-90' : isActiveDay ? '' : 'opacity-70'} />
              {/* Crossed-off base + final amount inline, same size */}
              {showSCBonus && (
                <span className="text-lg font-bold text-emerald-600/35 line-through decoration-emerald-600/50 decoration-2 leading-none">
                  {formatSC(day.sc)}
                </span>
              )}
              <span className={`text-lg font-bold leading-none ${
                isClaimed ? 'text-emerald-200' :
                isActiveDay ? 'text-green-300' :
                'text-emerald-600/70'
              }`}>
                {formatSC(finalSC)}
              </span>
            </div>
          )}
        </div>

        {/* Claimed checkmark */}
        {isClaimed && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full flex items-center justify-center shadow-lg border border-white">
            <svg className="w-3 h-3 text-green-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}

        {/* Current day pulse */}
        {isActiveDay && (
          <div className="absolute inset-0 rounded-xl bg-yellow-300/30 animate-pulse pointer-events-none" />
        )}
      </div>
    );
  }

  // Standard vertical layout for Days 1-6 (and Day 7 on desktop)
  return (
    <div
      className={`
        relative flex flex-col items-center justify-center rounded-xl py-2 px-1 transition-all h-full
        ${isClaimed ? 'bg-green-600/70 ring-2 ring-green-400 shadow-[0_0_15px_rgba(34,197,94,0.3)]' : ''}
        ${isActiveDay ? 'bg-amber-500/70 ring-2 ring-amber-300 shadow-[0_0_20px_rgba(251,191,36,0.5)] scale-105' : ''}
        ${isUpcoming ? 'bg-muted/50 border border-border' : ''}
        ${isExpired ? 'bg-muted/30 opacity-50' : ''}
      `}
    >
      {/* Day Label */}
      <span className={`text-[9px] font-bold uppercase leading-none mb-1 ${
        isClaimed ? 'text-green-100' :
        isActiveDay ? 'text-yellow-100' :
        'text-muted-foreground'
      }`}>
        Day {day.day === 7 ? '7+' : day.day}
      </span>

      {/* Rewards with GC/SC badges */}
      <div className="flex flex-col items-center gap-0.5">
        {/* GC Reward */}
        <div className={`flex items-center gap-1 ${
          isUpcoming || isExpired ? 'opacity-70' : ''
        }`}>
          <GCBadgeSVG size={20} className={isClaimed ? 'opacity-90' : isActiveDay ? '' : 'opacity-70'} />
          {/* Crossed-off base + final amount inline */}
          {showGCBonus && (
            <span className="text-[11px] font-black text-foreground/30 line-through decoration-foreground/40 decoration-2 leading-none">
              {formatGC(day.gc)}
            </span>
          )}
          <span className={`text-[11px] font-black leading-none ${
            isClaimed ? 'text-white' :
            isActiveDay ? 'text-amber-200' :
            'text-foreground/70'
          }`}>
            {formatGC(finalGC)}
          </span>
        </div>
        {/* SC Reward */}
        {hasSC && (
          <div className={`flex items-center gap-1 ${
            isUpcoming || isExpired ? 'opacity-70' : ''
          }`}>
            <SCBadgeSVG size={20} className={isClaimed ? 'opacity-90' : isActiveDay ? '' : 'opacity-70'} />
            {/* Crossed-off base + final amount inline */}
            {showSCBonus && (
              <span className="text-[10px] font-bold text-emerald-600/35 line-through decoration-emerald-600/50 decoration-2 leading-none">
                {formatSC(day.sc)}
              </span>
            )}
            <span className={`text-[10px] font-bold leading-none ${
              isClaimed ? 'text-emerald-200' :
              isActiveDay ? 'text-green-300' :
              'text-emerald-600/70'
            }`}>
              {formatSC(finalSC)}
            </span>
          </div>
        )}
      </div>

      {/* Claimed checkmark */}
      {isClaimed && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full flex items-center justify-center shadow-lg border border-white">
          <svg className="w-2.5 h-2.5 text-green-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      {/* Current day pulse */}
      {isActiveDay && (
        <div className="absolute inset-0 rounded-xl bg-yellow-300/30 animate-pulse pointer-events-none" />
      )}
    </div>
  );
};
